import React, { Fragment, useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import classNames from 'classnames';
import { Header, Icon, LoadingIndicatorProgress, TooltipClipboard } from '@ohif/ui';
import { clearStudiesStorage, useAppConfig } from '@state';
import HttpClient from '@utils/HttpClient';
import { updateMenuOptions } from '@utils/navigation';
import { ActivityLineChart, DonutChart, ExamFunnelChart, GrowthLineChart, ServiceBarChart } from './Charts'
import { glowClassMap, processData} from './utils'
import Footer from '../../components/Footer';

import './Dashboard.css';


const MetricCard = ({ title, value, icon, className = '', changeType = 0, changeValue = '' }) => {
    const themeClass = glowClassMap[className] || 'theme-gray';

    return (
        <div
            className={classNames('flex-1 p-5 rounded-2xl transition-all duration-200 hover:scale-[1.02] dashboard-metric-card', themeClass)}
        >
            <p className="text-xs font-semibold uppercase tracking-widest mb-2 dashboard-metric-title">{title}</p>
            <h2 className="text-3xl font-bold dashboard-metric-value">{value}</h2>
            {changeValue && (
                <div className={classNames('mt-3 text-xs flex items-center gap-1', {
                    'text-green-400': changeType > 0,
                    'text-red-400': changeType < 0,
                    'text-gray-500': changeType === 0,
                })}>
                    <span className="font-semibold">{changeValue}</span>
                    <span className="dashboard-metric-subtext">vs Mês Passado</span>
                </div>
            )}
        </div>
    );
};


function TableWidths({ cols }) {
	const totalDefinedWidth = cols.reduce((sum, col) => sum + (col.width || 0), 0);
	const remainingWidth = 100 - totalDefinedWidth;
	const autoCols = cols.filter(col => !col.width).length;
	const autoWidth = autoCols > 0 ? remainingWidth / autoCols : 0;

	return (
		<colgroup>
			{cols.map((col, index) => (
				<col
                    key={index}
                    style={{ width: col.width ? `${col.width}%` : `${autoWidth}%` }}
                />
			))}
		</colgroup>
	)
}


const TableWithPagination = ({ data, columns, title, searchFields, renderExpandedContent, headerActions }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [expandedRow, setExpandedRow] = useState(null);
    const itemsPerPage = 10;
    const toggleRow = (id) => {
        setExpandedRow(expandedRow === id ? null : id);
    };

    // Lógica de Filtragem
    const filteredData = useMemo(() => {
        if (!searchTerm) return data;

        return data.filter(item =>
            searchFields.some(field =>
                String(item[field])?.toLowerCase().includes(searchTerm.toLowerCase())
            )
        );
    }, [data, searchTerm, searchFields]);

    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        const end = start + itemsPerPage;
        return filteredData.slice(start, end);
    }, [filteredData, currentPage, itemsPerPage]);

    useEffect(() => {
        setCurrentPage(1);
        setExpandedRow(null); // Fecha a linha expandida ao mudar a busca
    }, [searchTerm]);

    const renderCell = (item, column) => {
        const value = item[column.key];
        switch (column.key) {
            case 'srvc_name':
                return value.toUpperCase();
            case 'reportId':
            case 'client_id':
            case 'email':
                return <TooltipClipboard text={value || 'N/A'}>{value || 'N/A'}</TooltipClipboard>;
            case 'is_active':
            case 'is_staff':
            case 'is_superuser':
            case 'is_checked':
            case 'has_feedback':
            case 'can_upload':
                return (
                    <span className={classNames("px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full", {
                        "bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100": value,
                        "bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100": !value,
                    })}>
                        {value ? 'Sim' : 'Não'}
                    </span>
                );
            case 'report_status':
            case 'job_status':
                return (
                    <span className={classNames('px-2 py-0.5 rounded-full text-xs font-semibold', {
                        'bg-green-600/30 text-green-300': value === 'Viewer OK' || value === 'Reported Success' || value === 'Visualizado' || value === 'Editado',
                        'bg-yellow-600/30 text-yellow-300': value === 'Processing',
                        'bg-red-600/30 text-red-300': value === 'Reported Error' || value === 'Invalid file' || value === 'Failed' || value === 'Não visualizado',
                        'bg-gray-600/30 text-white-300': value === 'Finished' || value === '',
                    })}>
                        {value || 'N/A'}
                    </span>
                );
            default:
                return typeof value === 'object' && value !== null ? value.toString() : value;
        }
    };

    return (
        <div className="rounded-2xl mt-6 overflow-hidden dashboard-table-container">
            <div className="flex flex-col md:flex-row gap-3 justify-between items-start md:items-center p-5 dashboard-table-toolbar">
                <div className="w-full md:w-1/2 relative">
                    <Icon name="icon-search" className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 dashboard-search-icon" />
                    <input
                        type="text"
                        placeholder="Buscar..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 rounded-xl text-sm text-white placeholder-slate-600 focus:outline-none transition-all dashboard-search-input"
                    />
                </div>
                {headerActions && <div className="flex-shrink-0">{headerActions}</div>}
            </div>

            <div className="overflow-x-auto">
                <table className="min-w-full table-fixed">
                    <TableWidths cols={columns} />
                    <thead>
                        <tr className="text-left text-xs uppercase tracking-widest dashboard-table-header-row">
                            {columns.map(col => (
                                <th key={col.key} className="px-6 py-3 font-semibold">{col.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="text-sm divide-y dashboard-table-body">
                        {paginatedData.length > 0 ? (
                            paginatedData.map((item, index) => (
                                <Fragment key={item.id || index}>
                                    <tr
                                        className={classNames('cursor-pointer dashboard-table-row', {
                                            'is-expanded': expandedRow === item.id,
                                        })}
                                        onClick={() => renderExpandedContent && toggleRow(item.id)}
                                    >
                                        {columns.map(col => (
                                            <td key={col.key} className="px-6 py-3.5 overflow-hidden text-ellipsis max-w-0 dashboard-table-cell">
                                                {renderCell(item, col)}
                                            </td>
                                        ))}
                                    </tr>
                                    {expandedRow === item.id && renderExpandedContent && (
                                        <tr>
                                            <td colSpan={columns.length} className="p-0">
                                                <div className="pl-8 p-4 dashboard-expanded-content">
                                                    {renderExpandedContent(item)}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={columns.length} className="px-6 py-8 text-center dashboard-empty-cell">
                                    Nenhum resultado encontrado.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <div className="flex justify-between items-center px-5 py-3 dashboard-table-pagination">
                    <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="flex items-center justify-center w-8 h-8 rounded-lg transition-all disabled:opacity-30 dashboard-pagination-btn"
                    >
                        <Icon name="chevron-prev" className="w-4" />
                    </button>
                    <span className="text-xs dashboard-pagination-info">Página {currentPage} de {totalPages}</span>
                    <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="flex items-center justify-center w-8 h-8 rounded-lg transition-all disabled:opacity-30 dashboard-pagination-btn"
                    >
                        <Icon name="chevron-next" className="w-4" />
                    </button>
                </div>
            )}
        </div>
    );
};


export default function Dashboard() {
    const { appConfig } = useAppConfig();
    const [isStaff] = useState(appConfig.session.isStaff);
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const [dashboardData, setDashboardData] = useState(null);

    const tabs = [
        { id: 'overview', title: t('Visão Geral'), iconPath: '/public/assets/icons/icon-overview-2.svg' },
        { id: 'users', title: t('Usuários'), iconPath: '/public/assets/icons/icon-users-2.svg' },
        { id: 'workspaces', title: t('Workspaces'), iconPath: '/public/assets/icons/icon-workspace.svg' },
        { id: 'services', title: t('Serviços'), iconPath: '/public/assets/icons/icon-service.svg' },
        { id: 'exams', title: t('Exames'), iconPath: '/public/assets/icons/icon-exam-2.svg' },
    ];

    const menuOptions = [
        {
            icon: 'power-off',
            title: t('Header:Logout'),
            onClick: async () => {
                navigate('/login', { replace: true });
                clearStudiesStorage();
            },
        },
    ];

    if (isStaff) {
        updateMenuOptions(menuOptions, navigate, t)
    }

    useEffect(() => {
        async function loadData() {
            try {
                const resp = await HttpClient.get(`/workspaces/overview`);
                if (resp.ok) {
                    const { data } = resp;
                    const processed = processData(data);
                    setDashboardData(processed);
                    setLoading(false);
                } else {
                    toast.error('Erro ao carregar dados');
                    navigate('/', { replace: true });
                }
            } catch (error) {
                console.error('Erro ao carregar dados:', error);
                setLoading(false);
            }
        }

        document.title = 'HarpiAI APP | Dashboard';
        loadData();
    }, []);

    if (loading || !dashboardData) {
        return <LoadingIndicatorProgress className="h-full w-full bg-black" />;
    }

    const TabButton = ({ id, title, iconPath, isActive, onClick }) => (
        <button
            onClick={() => onClick(id)}
            className={classNames('flex flex-1 items-center gap-2.5 px-4 py-2.5 rounded-xl transition-all duration-200 font-semibold text-sm justify-center dashboard-tab-btn', {
                'is-active': isActive,
            })}
        >
            <img className="w-7 h-7" src={iconPath} alt="" />
            <span className="hidden sm:block">{title}</span>
        </button>
    );

    // Componentes Wrapper para Tabelas (para usar o TableWithPagination)
    const UserTable = (props) => <TableWithPagination {...props} />;
    const WorkspaceTable = (props) => <TableWithPagination {...props} />;
    const ServiceTable = (props) => <TableWithPagination {...props} />;
    const ExamListTable = (props) => <TableWithPagination {...props} />;

    // --- Funções de Conteúdo Expandido ---
    const renderExpandedUserDetails = (user) => (
        <div className="text-white grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
                <p className="text-gray-400 font-medium">Workspaces:</p>
                <p className="text-xl font-bold text-purple-400">{user.workspacesCount}</p>
            </div>

            <div>
                <p className="text-gray-400 font-medium">Serviços Ativos:</p>
                <p className="text-xl font-bold text-green-400">{user.activeServicesCount}</p>
            </div>

            <div>
                <p className="text-gray-400 font-medium">Filtros criados:</p>
                <p className="text-xl font-bold text-blue-400">{user.createdFilters}</p>
            </div>

            <div>
                <p className="text-gray-400 font-medium">Último Exame Enviado:</p>
                <p className="text-xl font-bold text-yellow-400">{user.lastExamDateFormatted}</p>
            </div>
        </div>
    );

    const renderExpandedWorkspaceUsers = (workspace) => (
        <div className="text-white grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
                <h4 className="text-lg font-semibold mb-2 text-blue-400">Usuários no Workspace: {workspace.userCount}</h4>
                <div className="flex flex-wrap gap-2">
                    {workspace.usersInWorkspace.length > 0 ? (
                        workspace.usersInWorkspace.map((user, index) => (
                            <span key={index} className="bg-gray-700 rounded-full px-3 py-1 text-sm">
                                {user.username} {user.is_staff ? '(Staff)' : ''}
                            </span>
                        ))
                    ) : (
                        <span className="text-gray-400">Nenhum usuário ativo neste workspace.</span>
                    )}
                </div>
            </div>

            <div>
                <h4 className="text-lg font-semibold mb-2 text-blue-400">Serviços habilitados: {workspace.services.length}</h4>
                <div className="flex flex-wrap gap-2">
                    {workspace.services.length > 0 ? (
                        workspace.services.map((service, index) => (
                            <span key={index} className="bg-gray-700 rounded-full px-3 py-1 text-sm">
                                {service}
                            </span>
                        ))
                    ) : (
                        <span className="text-gray-400">Nenhum serviço habilitado neste workspace.</span>
                    )}
                </div>
            </div>
        </div>
    );

    // --- Renderização das Abas ---
    const renderOverview = () => {
        const examsByMonthData = dashboardData.monthlyActivity.uploads;
        const examStatusChartData = dashboardData.examStatusData;
        const funnelData = Object.entries(dashboardData.funnelCounts).map(([key, count]) => ({ label: key, count }));

        const activityCards = [
            { title: 'Usuários Ativos', value: dashboardData.activeUsers.daily, icon: 'user', className: 'bg-indigo-700', changeType: 1, changeValue: '' },
            { title: 'Total de Uploads', value: dashboardData.totalExams, icon: 'upload', className: 'bg-blue-700', changeType: 1, changeValue: '' },
            { title: 'Total de Workspaces', value: dashboardData.totalWorkspaces, icon: 'server', className: 'bg-purple-700', changeType: 0, changeValue: '' },
            { title: 'Total de Feedbacks', value: dashboardData.totalFeedbacks, icon: 'comment', className: 'bg-pink-700', changeType: 1, changeValue: '' },
        ];

        return (
            <Fragment>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
                    {activityCards.map((card, index) => (
                        <MetricCard key={index} {...card} />
                    ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
                    <div className="lg:col-span-2 p-5 rounded-2xl dashboard-card h-[350px] sm:h-[400px]">
                        <ActivityLineChart data={examsByMonthData} title="Volume de Uploads" />
                    </div>
                    <div className="p-5 rounded-2xl relative dashboard-card h-[350px] sm:h-[400px]">
                        <DonutChart title="Status de Processamento" data={examStatusChartData} totalLabel="Total" />
                    </div>
                </div>

                <div className="p-5 rounded-2xl h-[400px] dashboard-card">
                    <ExamFunnelChart data={funnelData} />
                </div>
            </Fragment>
        );
    };

    const renderUsers = () => {
        const columns = [
            { key: 'dateJoined', label: 'Criado em', width: 20, },
            { key: 'username', label: 'Usuário', width: 20, },
            { key: 'email', label: 'E-mail', width: 20, },
            { key: 'uploads', label: 'Uploads', width: 10, },
            { key: 'approvals', label: 'Aprovações', width: 10, },
            { key: 'is_staff', label: 'Staff', width: 10, },
            { key: 'is_active', label: 'Habilitado', width: 10, },
        ];
        const searchFields = ['username', 'email', 'id'];

        // Cards de Usuário
        const userCards = [
            {
                title: 'Total de Usuários',
                value: dashboardData.totalUsers,
                icon: 'users',
                className: 'bg-indigo-700'
            },
            {
                title: 'Usuários habilitados',
                value: dashboardData.usersOn,
                icon: 'users',
                className: 'bg-green-700'
            },
            {
                title: 'Staff Ativo',
                value: dashboardData.userList.filter(u => u.is_staff).length,
                icon: 'lock',
                className: 'bg-blue-600'
            },
            {
                title: 'Usuários Ativos Hoje',
                value: dashboardData.activeUsers.daily,
                icon: 'user',
                className: 'bg-blue-900'
            },
            {
                title: 'Usuários Ativos esta semana',
                value: dashboardData.activeUsers.weekly,
                icon: 'user',
                className: 'bg-blue-900'
            },
            {
                title: 'Usuários Ativos este mês',
                value: dashboardData.activeUsers.monthly,
                icon: 'user',
                className: 'bg-blue-900'
            },
        ];

        return (
            <Fragment>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-5 mb-8">
                    {userCards.map((card, index) => (
                        <MetricCard key={index} {...card} changeValue="" />
                    ))}
                </div>

                <div className="h-[400px] mb-6 p-5 rounded-2xl dashboard-card">
                    <GrowthLineChart data={dashboardData.monthlyActivity.users} title="Crescimento Acumulado de Usuários" />
                </div>

                <UserTable
                    data={dashboardData.userList}
                    columns={columns}
                    title=""
                    searchFields={searchFields}
                    renderExpandedContent={renderExpandedUserDetails}
                />
            </Fragment>
        );
    };

    const renderWorkspaces = () => {
        const columns = [
            { key: 'createdAt', label: 'Criado em', width: 20},
            { key: 'name', label: 'Nome', width: 20},
            { key: 'id', label: 'ID', width: 20},
            { key: 'client_id', label: 'Client ID', width: 20},
            { key: 'ownerName', label: 'Dono', width: 20},
            { key: 'userCount', label: 'Usuários', width: 5},
            { key: 'can_upload', label: 'Upload', width: 5},
            { key: 'is_active', label: 'Ativo', width: 5},
        ];
        const searchFields = ['name', 'id', 'ownerName'];

        // Cards de Workspace
        const workspaceCards = [
            { title: 'Total de Workspaces', value: dashboardData.totalWorkspaces, icon: 'folder', className: 'bg-purple-700' },
            { title: 'Workspaces Ativos', value: dashboardData.activeWorkspaces, icon: 'check-circle', className: 'bg-green-700' },
            { title: 'Workspaces Upload', value: dashboardData.canUploadWorkspaces, icon: 'cloud-upload-alt', className: 'bg-blue-700' },
        ];

        const enrichedWorkspaceList = dashboardData.workspaceList.map(ws => ({
            ...ws,
            ownerName: dashboardData.userList.find(u => u.id === ws.owner)?.username || 'N/A',
            userCount: dashboardData.serviceList.filter(s => s.workspace === ws.id).map(s => s.user).filter((v, i, a) => a.indexOf(v) === i).length
        }));

        return (
            <Fragment>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
                    {workspaceCards.map((card, index) => (
                        <MetricCard key={index} {...card} changeValue="" />
                    ))}
                </div>

                <div className="h-[400px] mb-6 p-5 rounded-2xl dashboard-card">
                    <GrowthLineChart data={dashboardData.monthlyActivity.workspaces} title="Crescimento Acumulado de Workspaces" />
                </div>

                <WorkspaceTable
                    data={enrichedWorkspaceList}
                    columns={columns}
                    title=""
                    searchFields={searchFields}
                    renderExpandedContent={renderExpandedWorkspaceUsers}
                />
            </Fragment>
        );
    };

    const renderServices = () => {
        const columns = [
            { key: 'createdAt', label: 'Criado em', width: 20, },
            { key: 'userName', label: 'Usuário', width: 20, },
            { key: 'service', label: 'Serviço', width: 20, },
            { key: 'workspaceName', label: 'Workspace', width: 20, },
            { key: 'can_upload', label: 'Upload', width: 10, },
            { key: 'is_active', label: 'Ativo', width: 10, },
        ];
        const searchFields = ['service', 'workspaceName', 'userName'];

        const enrichedServiceList = dashboardData.serviceList.map(s => ({
            ...s,
            userName: dashboardData.userList.find(u => u.id === s.user)?.username || 'N/A',
            workspaceName: dashboardData.workspaceList.find(w => w.id === s.workspace)?.name || 'N/A',
        }));

        return (
            <Fragment>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-8">
                    <MetricCard title="Total de Permissões Ativas" value={dashboardData.activePermissions} icon="lock-open" className="bg-blue-700" changeValue="" />
                    <MetricCard title="Permissões com Upload" value={dashboardData.uploadPermissions} icon="cloud-upload-alt" className="bg-green-700" changeValue="" />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
                    <div className="p-5 rounded-2xl dashboard-card h-[350px] sm:h-[400px]">
                        <ServiceBarChart data={dashboardData.serviceUsageCounts} />
                    </div>
                    <div className="p-5 rounded-2xl dashboard-card h-[350px] sm:h-[400px]">
                        <GrowthLineChart data={dashboardData.monthlyActivity.services} title="Crescimento Acumulado de Serviços Ativos" />
                    </div>
                </div>

                <ServiceTable data={enrichedServiceList} columns={columns} title="" searchFields={searchFields} />
            </Fragment>
        );
    };

    const renderExams = () => {
        const columns = [
            { key: 'createdAt', label: 'Data', width: 10 },
            { key: 'reportId', label: 'Report ID', width: 20 },
            { key: 'srvc_name', label: 'Serviço', width: 10 },
            { key: 'uploader', label: 'Enviado por', width: 20 },
            { key: 'job_status', label: 'Status', width: 20 },
            { key: 'report_status', label: 'Laudo', width: 20 },
            { key: 'approver', label: 'Aprovado por', width: 20 },
            { key: 'has_feedback', label: 'Feedback', width: 10 },
        ];
        const searchFields = ['reportId', 'srvc_name', 'uploader', 'job_status', 'report_status', 'approver'];

        // Métricas baseadas na lista completa de exames
        const totalExams = dashboardData.examList.length;
        const totalApproved = dashboardData.examList.filter(e => e.approved_by).length;
        const totalProcessing = dashboardData.examList.filter(e => e.job_status === 'Processing').length;
        const totalFeedbacks = dashboardData.examList.filter(e => e.has_feedback).length;

        const serviceCounts = { CCT: 0, CXR: 0, DO: 0, MAMMO: 0, MSKXR: 0 };
        dashboardData.examList.forEach(e => {
            const srv = (e.srvc_name || 'Desconhecido').toUpperCase();
            serviceCounts[srv] = (serviceCounts[srv] || 0) + 1;
        });

        return (
            <Fragment>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mb-8">
                    <MetricCard title="Total Exames" value={totalExams} icon="stethoscope" className="bg-blue-700" changeValue="" />
                    <MetricCard title="Aprovados" value={totalApproved} icon="check-double" className="bg-green-700" changeValue="" />
                    <MetricCard title="Aguardando Process." value={totalProcessing} icon="spinner" className="bg-gray-700" changeValue="" />
                    <MetricCard title="Com Feedback" value={totalFeedbacks} icon="comment-dots" className="bg-pink-700" changeValue="" />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                    {Object.entries(serviceCounts).map(([srv, count]) => (
                        <div key={srv} className="p-3 rounded-xl dashboard-service-card">
                            <p className="text-xs font-bold uppercase tracking-widest mb-1 dashboard-service-card-title">{srv}</p>
                            <p className="text-2xl font-bold dashboard-service-card-value">{count}</p>
                        </div>
                    ))}
                </div>

                <ExamListTable
                    data={dashboardData.examList}
                    columns={columns}
                    title=""
                    searchFields={searchFields}
                    headerActions={
                        <button
                            onClick={() => exportToCsv(dashboardData.examList, columns, `exames_${new Date().toISOString()}.csv`)}
                            title="Exportar CSV"
                            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all dashboard-btn-csv"
                        >
                            <Icon name="icon-save" />
                            CSV
                        </button>
                    }
                />
            </Fragment>
        );
    };


    const renderTabContent = () => {
        switch (activeTab) {
            case 'overview':
                return renderOverview();
            case 'users':
                return renderUsers();
            case 'workspaces':
                return renderWorkspaces();
            case 'services':
                return renderServices();
            case 'exams':
                return renderExams();
            default:
                return <p className="text-white">Selecione uma aba.</p>;
        }
    };

    return (
        <div className="flex h-screen flex-col dashboard-container">
            <Header
                isSticky
                menuOptions={menuOptions}
                isReturnEnabled={true}
                WhiteLabeling={appConfig.whiteLabeling}
                onClickReturnButton={() => navigate(-1)}
            />

            <div className="p-4 ohif-scrollbar flex grow flex-col overflow-y-auto">
                <div className="container max-w-[1420px] relative m-auto mt-0 pb-16">
                    <div className="mb-6">
                        <div className="flex flex-wrap gap-3">
                            {tabs.map(tab => (
                                <TabButton
                                    key={tab.id}
                                    id={tab.id}
                                    title={tab.title}
                                    iconPath={tab.iconPath}
                                    isActive={activeTab === tab.id}
                                    onClick={setActiveTab}
                                />
                            ))}
                        </div>
                    </div>

                    {renderTabContent()}
                </div>
            </div>

            <Footer />
        </div>
    );
};
